/**
 * Custom Header - Coldays.com Replica
 * Cart Drawer + Dynamic Search Slider UI
 * Updated: Fixed cart page sync + money format regex
 */

class CustomHeader {
  constructor() {
    this.wrapper = document.querySelector('.custom-header-wrapper');
    this.header = this.wrapper?.querySelector('.custom-header');
    this.menuToggle = this.wrapper?.querySelector('.js-menu-toggle');
    this.menuClose = this.wrapper?.querySelector('.js-menu-close');
    this.mobileMenu = this.wrapper?.querySelector('.js-mobile-menu');
    this.menuOverlay = this.wrapper?.querySelector('.js-menu-overlay');

    this.cartToggle = this.wrapper?.querySelector('.js-cart-toggle');
    this.cartClose = this.wrapper?.querySelector('.js-cart-close');
    this.cartCloseSecondary = this.wrapper?.querySelector('.js-cart-close-secondary');
    this.cartDrawer = this.wrapper?.querySelector('.js-cart-drawer');
    this.cartOverlay = this.wrapper?.querySelector('.js-cart-overlay');
    this.cartContent = this.wrapper?.querySelector('.js-cart-content');
    this.cartFooter = this.wrapper?.querySelector('.js-cart-footer');
    this.cartSubtotal = this.wrapper?.querySelector('.js-cart-subtotal');
    this.cartPrice = this.wrapper?.querySelector('[data-cart-price]');

    // Search elements
    this.searchToggles = this.wrapper?.querySelectorAll('.js-search-toggle');
    this.searchClose = this.wrapper?.querySelector('.js-search-close');
    this.searchDropdown = this.wrapper?.querySelector('.js-search-dropdown');
    this.searchInput = this.wrapper?.querySelector('.js-search-input');
    this.searchClear = this.wrapper?.querySelector('.js-search-clear');
    this.searchSubmit = this.wrapper?.querySelector('.js-search-submit');
    this.searchSidebar = this.wrapper?.querySelector('.js-search-sidebar');
    this.searchProductsGrid = this.wrapper?.querySelector('.js-search-products-grid');
    this.searchProductsTitle = this.wrapper?.querySelector('.js-search-products-title');
    this.searchFor = this.wrapper?.querySelector('.js-search-for');
    this.searchForLink = this.wrapper?.querySelector('.js-search-for-link');
    this.searchForTerm = this.wrapper?.querySelector('.js-search-for-term');

    this.isTransparent = this.wrapper?.dataset.transparent === 'true';
    this.scrollThreshold = 50;
    this.searchDebounceTimer = null;
    this.defaultProductsLoaded = false;
    this.lastCartTotal = null;
    this.pollingInterval = null;

    this.init();
  }

  init() {
    if (!this.wrapper) return;
    this.bindEvents();
    this.handleScroll();
    this.updateCartPrice();

    // Start polling to catch cart changes from cart page, quick view, etc.
    this.startCartPolling();

    document.addEventListener('cart:updated', this.updateCartPrice.bind(this));
    document.addEventListener('cart:refresh', this.updateCartPrice.bind(this));
  }

  bindEvents() {
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Mobile menu
    this.menuToggle?.addEventListener('click', this.openMenu.bind(this));
    this.menuClose?.addEventListener('click', this.closeMenu.bind(this));
    this.menuOverlay?.addEventListener('click', this.closeMenu.bind(this));

    // Cart drawer
    this.cartToggle?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openCart();
    });
    this.cartClose?.addEventListener('click', this.closeCart.bind(this));
    this.cartCloseSecondary?.addEventListener('click', this.closeCart.bind(this));
    this.cartOverlay?.addEventListener('click', this.closeCart.bind(this));

    // Search dropdown
    this.searchToggles?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSearch();
      });
    });
    this.searchClose?.addEventListener('click', this.closeSearch.bind(this));
    this.searchInput?.addEventListener('input', this.handleSearchInput.bind(this));
    this.searchClear?.addEventListener('click', this.clearSearch.bind(this));
    this.searchSubmit?.addEventListener('click', () => {
      const query = this.searchInput.value.trim();
      if (query) window.location.href = `/search?q=${encodeURIComponent(query)}`;
    });
    this.searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const query = this.searchInput.value.trim();
        if (query) window.location.href = `/search?q=${encodeURIComponent(query)}`;
      }
    });

    // Close search when clicking outside
    document.addEventListener('click', (e) => {
      if (this.searchDropdown?.classList.contains('is-open')) {
        const isClickInside = this.searchDropdown.contains(e.target) ||
          Array.from(this.searchToggles).some(t => t.contains(e.target));
        if (!isClickInside) this.closeSearch();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMenu();
        this.closeCart();
        this.closeSearch();
      }
    });

    this.bindCartEvents();
  }

  // Poll cart frequently to catch changes from cart page
  startCartPolling() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);

    // Poll every 800ms
    this.pollingInterval = setInterval(() => {
      this.fetchCartAndUpdate();
    }, 800);

    // Poll immediately when page becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.fetchCartAndUpdate();
      }
    });
  }

  handleScroll() {
    const scrollY = window.scrollY || window.pageYOffset;
    if (scrollY > this.scrollThreshold) {
      if (!this.wrapper.classList.contains('is-scrolled')) {
        this.wrapper.classList.add('is-scrolled');
      }
    } else {
      if (this.wrapper.classList.contains('is-scrolled')) {
        this.wrapper.classList.remove('is-scrolled');
      }
    }
  }

  openMenu() {
    this.mobileMenu?.classList.add('is-open');
    this.menuToggle?.classList.add('is-active');
    this.menuToggle?.setAttribute('aria-expanded', 'true');
    this.mobileMenu?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
    this.trapFocus(this.mobileMenu);
  }

  closeMenu() {
    this.mobileMenu?.classList.remove('is-open');
    this.menuToggle?.classList.remove('is-active');
    this.menuToggle?.setAttribute('aria-expanded', 'false');
    this.mobileMenu?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
    this.menuToggle?.focus();
  }

  openCart() {
    this.fetchCartAndRender();
    this.cartDrawer?.classList.add('is-open');
    this.cartToggle?.setAttribute('aria-expanded', 'true');
    this.cartDrawer?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.trapFocus(this.cartDrawer);
  }

  closeCart() {
    this.cartDrawer?.classList.remove('is-open');
    this.cartToggle?.setAttribute('aria-expanded', 'false');
    this.cartDrawer?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.cartToggle?.focus();
  }

  toggleSearch() {
    if (this.searchDropdown?.classList.contains('is-open')) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    this.searchDropdown?.classList.add('is-open');
    this.searchDropdown?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    this.searchDropdown?.classList.remove('is-searching');

    this.searchInput.value = '';
    this.searchClear.style.display = 'none';
    this.searchFor.style.display = 'none';
    this.searchProductsTitle.style.display = 'none';

    if (!this.defaultProductsLoaded) {
      this.loadDefaultProducts();
    } else {
      this.searchProductsGrid.innerHTML = this.defaultProductsHtml || '';
    }

    setTimeout(() => this.searchInput?.focus(), 100);
  }

  closeSearch() {
    this.searchDropdown?.classList.remove('is-open');
    this.searchDropdown?.setAttribute('aria-hidden', 'true');
    this.searchDropdown?.classList.remove('is-searching');
    document.body.classList.remove('search-open');
    this.searchToggles?.[0]?.focus();
  }

  async loadDefaultProducts() {
    try {
      const response = await fetch('/search/suggest.json?q=shirt&resources[type]=product&resources[limit]=10&resources[options][fields]=title,variants.title,vendor,product_type,tag');
      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (products.length > 0) {
        this.defaultProductsHtml = this.renderProductCards(products);
        this.searchProductsGrid.innerHTML = this.defaultProductsHtml;
        this.defaultProductsLoaded = true;
      } else {
        this.searchProductsGrid.innerHTML = '<div style="font-family:var(--font-header);font-size:12px;color:#888;padding:2rem 0;">No products available</div>';
      }
    } catch (err) {
      console.error('Default products error:', err);
      this.searchProductsGrid.innerHTML = '<div style="font-family:var(--font-header);font-size:12px;color:#888;padding:2rem 0;">Loading products...</div>';
    }
  }

  handleSearchInput(e) {
    const query = e.target.value.trim();
    this.searchClear.style.display = query.length > 0 ? 'flex' : 'none';

    clearTimeout(this.searchDebounceTimer);

    if (query.length === 0) {
      this.searchDropdown.classList.remove('is-searching');
      this.searchFor.style.display = 'none';
      this.searchProductsTitle.style.display = 'none';
      this.searchProductsGrid.innerHTML = this.defaultProductsHtml || '';
      return;
    }

    this.searchDropdown.classList.add('is-searching');
    this.searchProductsTitle.style.display = 'block';
    this.searchProductsGrid.innerHTML = '<div style="font-family:var(--font-header);font-size:12px;color:#888;padding:3rem 0;width:100%;text-align:left;">Searching...</div>';
    this.searchFor.style.display = 'none';

    this.searchDebounceTimer = setTimeout(() => {
      this.fetchSearchResults(query);
    }, 300);
  }

  clearSearch() {
    this.searchInput.value = '';
    this.searchClear.style.display = 'none';
    this.searchFor.style.display = 'none';
    this.searchProductsTitle.style.display = 'none';
    this.searchDropdown.classList.remove('is-searching');
    this.searchProductsGrid.innerHTML = this.defaultProductsHtml || '';
    this.searchInput.focus();
  }

  async fetchSearchResults(query) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,variants.title,vendor,product_type,tag`);
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      this.renderSearchResults(products, query);
    } catch (error) {
      console.error('Search error:', error);
      this.searchProductsGrid.innerHTML = '<div style="font-family:var(--font-header);font-size:12px;color:#888;padding:3rem 0;width:100%;">No results found</div>';
    }
  }

  renderSearchResults(products, query) {
    if (products.length === 0) {
      this.searchProductsGrid.innerHTML = '<div style="font-family:var(--font-header);font-size:12px;color:#888;padding:3rem 0;width:100%;">No products found</div>';
      this.searchProductsTitle.textContent = 'Products';
      this.searchFor.style.display = 'block';
      this.searchForTerm.textContent = query;
      this.searchForLink.href = `/search?q=${encodeURIComponent(query)}`;
      return;
    }

    this.searchProductsTitle.textContent = 'PRODUCTS';
    this.searchProductsGrid.innerHTML = this.renderProductCards(products);

    this.searchFor.style.display = 'block';
    this.searchForTerm.textContent = query;
    this.searchForLink.href = `/search?q=${encodeURIComponent(query)}`;
  }

  renderProductCards(products) {
    return products.map(product => {
      const hasDiscount = product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price);
      const discountPercent = hasDiscount ? Math.round((1 - parseFloat(product.price) / parseFloat(product.compare_at_price)) * 100) : 0;

      return `
        <a href="${product.url}" class="search-product-card">
          <div class="search-product-card__image-wrap">
            <img src="${product.image || ''}" alt="${product.title}" class="search-product-card__image" loading="lazy" onerror="this.style.display='none'">
            ${hasDiscount ? `<span class="search-product-card__badge">Sale</span>` : ''}
          </div>
          <h4 class="search-product-card__title">${this.escapeHtml(product.title)}</h4>
          <div class="search-product-card__price-row">
            <span class="search-product-card__price">${this.formatMoney(parseFloat(product.price) * 100)}</span>
            ${hasDiscount ? `<span class="search-product-card__compare">${this.formatMoney(parseFloat(product.compare_at_price) * 100)}</span>` : ''}
            ${hasDiscount ? `<span class="search-product-card__discount">-${discountPercent}%</span>` : ''}
          </div>
        </a>
      `;
    }).join('');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  trapFocus(element) {
    const focusable = element.querySelectorAll('a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    first?.focus();
  }

  bindCartEvents() {
    // Product page add-to-cart forms
    document.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', () => {
        setTimeout(() => {
          this.fetchCartAndUpdate();
          this.fetchCartAndRender();
        }, 800);
      });
    });

    // Cart page: intercept the update form
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', () => {
        setTimeout(() => this.fetchCartAndUpdate(), 100);
      });
    }

    // Listen for quantity changes on cart page (inputs)
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (
        target.matches('input[name="updates[]"]') ||
        target.matches('.cart__qty-input') ||
        target.matches('.quantity__input') ||
        target.matches('[name*="updates"]') ||
        target.closest('.cart__qty') ||
        target.closest('.quantity')
      ) {
        setTimeout(() => {
          this.fetchCartAndUpdate();
          this.fetchCartAndRender();
        }, 600);
      }
    });

    // Listen for cart page +/- button clicks
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (
        target.matches('.cart__qty-btn') ||
        target.matches('.qty-btn') ||
        target.matches('[data-quantity-button]') ||
        target.matches('.quantity-button') ||
        target.closest('.cart__qty-btn') ||
        target.closest('[data-quantity-button]')
      ) {
        setTimeout(() => {
          this.fetchCartAndUpdate();
          this.fetchCartAndRender();
        }, 600);
      }
    });

    // Shopify native cart events
    if (window.Shopify?.onCartUpdate) {
      const originalOnCartUpdate = window.Shopify.onCartUpdate;
      window.Shopify.onCartUpdate = () => {
        originalOnCartUpdate();
        this.fetchCartAndUpdate();
        this.fetchCartAndRender();
      };
    }
  }

  async fetchCartAndUpdate() {
    try {
      // CRITICAL FIX: Add cache-buster to prevent browser caching
      const cacheBuster = Date.now();
      const res = await fetch(`/cart.js?_=${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!res.ok) throw new Error('Cart fetch failed');

      const cart = await res.json();

      // Always update to ensure sync
      const isSame = this.lastCartTotal === cart.total_price;
      this.lastCartTotal = cart.total_price;
      this.updateCartPriceDisplay(cart.total_price, !isSame);

    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  updateCartPrice() {
    this.fetchCartAndUpdate();
  }

  updateCartPriceDisplay(totalPrice, animate) {
    if (!this.cartPrice) return;

    const formatted = this.formatMoney(totalPrice);

    // Only update DOM if text actually changed
    if (this.cartPrice.textContent.trim() !== formatted) {
      this.cartPrice.textContent = formatted;

      if (animate) {
        this.cartPrice.style.transition = 'none';
        this.cartPrice.style.transform = 'scale(1.1)';
        requestAnimationFrame(() => {
          this.cartPrice.style.transition = 'transform 0.2s ease';
          this.cartPrice.style.transform = 'scale(1)';
        });
      }
    }
  }

  async fetchCartAndRender() {
    try {
      const cacheBuster = Date.now();
      const res = await fetch(`/cart.js?_=${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!res.ok) throw new Error('Cart render fetch failed');

      const cart = await res.json();
      this.renderCart(cart);
      this.lastCartTotal = cart.total_price;
      this.updateCartPriceDisplay(cart.total_price, false);
    } catch (err) {
      console.error('Cart render error:', err);
    }
  }

  renderCart(cart) {
    if (!this.cartContent || !this.cartFooter || !this.cartSubtotal) return;

    if (cart.item_count === 0) {
      this.cartContent.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty</p>
          <a href="/collections/all" class="cart-drawer__continue">Continue shopping</a>
        </div>
      `;
      this.cartFooter.style.display = 'none';
      return;
    }

    const itemsHtml = cart.items.map((item, index) => `
      <div class="cart-drawer__item" data-line="${index + 1}" data-key="${item.key}">
        <img src="${item.image || ''}" alt="${item.title}" class="cart-drawer__item-image" loading="lazy">
        <div class="cart-drawer__item-details">
          <div>
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          </div>
          <div>
            <div class="cart-drawer__item-bottom">
              <div class="cart-drawer__quantity">
                <button class="cart-drawer__qty-btn js-qty-minus" data-line="${index + 1}" aria-label="Decrease quantity">−</button>
                <span class="cart-drawer__qty-value">${item.quantity}</span>
                <button class="cart-drawer__qty-btn js-qty-plus" data-line="${index + 1}" aria-label="Increase quantity">+</button>
              </div>
              <span class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</span>
            </div>
            <button class="cart-drawer__item-remove js-remove-item" data-line="${index + 1}">Remove</button>
          </div>
        </div>
      </div>
    `).join('');

    this.cartContent.innerHTML = `<div>${itemsHtml}</div>`;
    this.cartSubtotal.textContent = this.formatMoney(cart.total_price);
    this.cartFooter.style.display = 'block';

    this.cartContent.querySelectorAll('.js-qty-minus').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const line = e.target.dataset.line;
        const currentQty = parseInt(e.target.nextElementSibling.textContent);
        this.updateItem(line, currentQty > 1 ? currentQty - 1 : 0);
      });
    });

    this.cartContent.querySelectorAll('.js-qty-plus').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const line = e.target.dataset.line;
        const currentQty = parseInt(e.target.previousElementSibling.textContent);
        this.updateItem(line, currentQty + 1);
      });
    });

    this.cartContent.querySelectorAll('.js-remove-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.updateItem(e.target.dataset.line, 0);
      });
    });
  }

  async updateItem(line, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line), quantity: quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
      this.lastCartTotal = cart.total_price;
      this.updateCartPriceDisplay(cart.total_price, true);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error('Update error:', err);
    }
  }

  // CRITICAL FIX: Correct regex for Shopify money format
  formatMoney(cents) {
    const formatString = window.Shopify?.money_format || 'Rs. {{amount}}';

    if (typeof cents === 'string') {
      cents = parseInt(cents.replace(/[^0-9]/g, ''), 10) || 0;
    }

    let value = '';
    // FIXED: Use proper regex without double escaping
    const regex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0.00';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    }

    const match = formatString.match(regex);
    switch (match?.[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(regex, value);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CustomHeader());
} else {
  new CustomHeader();
}

document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId === document.querySelector('.custom-header-wrapper')?.dataset.sectionId) {
    new CustomHeader();
  }
});